/**
 * Mailchimp Integration Component
 * Manages Mailchimp API key connection, audience display, and sync
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { useToast } from '@/hooks/use-toast'
import { apiClient, type MailchimpSyncPreview } from '@/lib/api'
import { toDisplayDate } from '@/lib/utils'
import { GRACKLE_COLORS, CATEGORICAL, SEMANTIC } from '@/lib/theme'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import {
  Loader2,
  Link2,
  Unlink,
  AlertCircle,
  CheckCircle,
  Users,
  Mail,
  RefreshCw,
  Eye,
} from 'lucide-react'
import { explainError } from '@/lib/explain-error'

interface MailchimpAudience {
  id: string
  name: string
  member_count: number
}

interface MailchimpStatus {
  connected: boolean
  integration?: {
    account_name: string
    account_id: string
    server_prefix: string
    selected_audience_id?: string | null
    last_sync_date?: string
    audiences?: MailchimpAudience[]
  }
}

export function MailchimpIntegration() {
  const { toast } = useToast()
  const [loading, setLoading] = useState(true)
  const [connecting, setConnecting] = useState(false)
  const [status, setStatus] = useState<MailchimpStatus | null>(null)
  const [apiKey, setApiKey] = useState('')

  // Sync state
  const [syncing, setSyncing] = useState(false)
  const [syncProgress, setSyncProgress] = useState<string | null>(null)
  const [syncingAudienceId, setSyncingAudienceId] = useState<string | null>(null)
  const [settingAudienceId, setSettingAudienceId] = useState<string | null>(null)
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Dry-run preview state (read-only — no Mailchimp writes).
  const [previewOpen, setPreviewOpen] = useState(false)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [preview, setPreview] = useState<MailchimpSyncPreview | null>(null)

  const handlePreview = async () => {
    setPreviewLoading(true)
    setPreviewOpen(true)
    try {
      // Include the selected audience so the preview also diffs against its
      // current members (new-vs-update + subscribed downgrades).
      const audienceId = status?.integration?.selected_audience_id ?? undefined
      setPreview(await apiClient.getMailchimpSyncPreview(audienceId))
    } catch {
      setPreviewOpen(false)
      toast({
        title: 'Preview failed',
        description: 'Could not build the sync preview. Please try again.',
        variant: 'destructive',
      })
    } finally {
      setPreviewLoading(false)
    }
  }

  const selectedAudienceId = status?.integration?.selected_audience_id ?? null

  useEffect(() => {
    fetchStatus()
    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current)
    }
  }, [])

  const fetchStatus = async () => {
    try {
      setLoading(true)
      const data = await apiClient.getMailchimpStatus()
      setStatus(data)
    } catch (error) {
      console.error('Failed to fetch Mailchimp status:', error)
      toast({
        title: 'Error',
        description: 'Failed to fetch Mailchimp integration status',
        variant: 'destructive',
      })
    } finally {
      setLoading(false)
    }
  }

  const handleConnect = async (e: React.FormEvent) => {
    e.preventDefault()

    const trimmedKey = apiKey.trim()
    if (!trimmedKey) {
      toast({
        title: 'API key required',
        description: 'Please enter your Mailchimp API key',
        variant: 'destructive',
      })
      return
    }

    if (!trimmedKey.includes('-')) {
      toast({
        title: 'Invalid API key format',
        description: 'Mailchimp API keys end with a datacenter prefix (e.g., abc123-us2)',
        variant: 'destructive',
      })
      return
    }

    try {
      setConnecting(true)
      const result = await apiClient.connectMailchimp(trimmedKey)

      toast({
        title: 'Connected',
        description: `Connected to Mailchimp account: ${result.account_name}`,
      })

      setApiKey('')
      await fetchStatus()
    } catch (error: any) {
      console.error('Failed to connect Mailchimp:', error)
      toast({
        title: 'Connection failed',
        description:
          error?.response?.data?.error ||
          'Failed to connect to Mailchimp. Check your API key and try again.',
        variant: 'destructive',
      })
    } finally {
      setConnecting(false)
    }
  }

  const handleDisconnect = async () => {
    if (
      !window.confirm(
        'Are you sure you want to disconnect Mailchimp? This will stop all audience syncing.'
      )
    ) {
      return
    }

    try {
      setLoading(true)
      await apiClient.disconnectMailchimp()

      toast({
        title: 'Disconnected',
        description: 'Mailchimp integration disconnected successfully',
      })

      await fetchStatus()
    } catch (error: any) {
      console.error('Failed to disconnect Mailchimp:', error)
      toast({
        title: 'Error',
        description: explainError(error, 'Failed to disconnect Mailchimp'),
        variant: 'destructive',
      })
    } finally {
      setLoading(false)
    }
  }

  const pollSyncStatus = useCallback(
    (syncId: string) => {
      let attempts = 0
      const maxAttempts = 120 // 10 minutes at 5s intervals

      pollTimerRef.current = setInterval(async () => {
        attempts++

        try {
          const { sync } = await apiClient.getMailchimpSyncStatus(syncId)

          if (sync.sync_status === 'completed') {
            if (pollTimerRef.current) clearInterval(pollTimerRef.current)
            setSyncing(false)
            setSyncProgress(null)
            setSyncingAudienceId(null)

            toast({
              title: 'Sync Complete',
              description: `Synced ${sync.items_created || 0} new and ${sync.items_updated || 0} updated members.`,
            })

            await fetchStatus()
          } else if (sync.sync_status === 'failed') {
            if (pollTimerRef.current) clearInterval(pollTimerRef.current)
            setSyncing(false)
            setSyncProgress(null)
            setSyncingAudienceId(null)

            toast({
              title: 'Sync Failed',
              description: sync.error_message || 'An error occurred during sync',
              variant: 'destructive',
            })
          } else if (sync.sync_status === 'in_progress') {
            const processed = sync.items_processed || 0
            const created = sync.items_created || 0
            const updated = sync.items_updated || 0
            if (processed > 0) {
              setSyncProgress(`Processing: ${processed} items (${created} new, ${updated} updated)`)
            } else {
              setSyncProgress('Starting sync...')
            }
          } else if (attempts >= maxAttempts) {
            if (pollTimerRef.current) clearInterval(pollTimerRef.current)
            setSyncing(false)
            setSyncProgress(null)
            setSyncingAudienceId(null)

            toast({
              title: 'Sync Timeout',
              description:
                'Sync is taking longer than expected. It will continue in the background.',
            })
          }
        } catch (error) {
          console.error('Failed to poll sync status:', error)
          if (pollTimerRef.current) clearInterval(pollTimerRef.current)
          setSyncing(false)
          setSyncProgress(null)
          setSyncingAudienceId(null)
        }
      }, 3000)
    },
    [toast]
  )

  const handleSetAudience = async (audienceId: string) => {
    if (audienceId === selectedAudienceId) return
    try {
      setSettingAudienceId(audienceId)
      await apiClient.setMailchimpAudience(audienceId)
      toast({
        title: 'Sync audience set',
        description: 'New and updated contacts will sync to this audience.',
      })
      await fetchStatus()
    } catch (error: any) {
      console.error('Failed to set sync audience:', error)
      toast({
        title: 'Could not set sync audience',
        description: explainError(error, 'Please try again.'),
        variant: 'destructive',
      })
    } finally {
      setSettingAudienceId(null)
    }
  }

  const handleSync = async (audienceId: string, syncType: 'full' | 'incremental') => {
    try {
      setSyncing(true)
      setSyncingAudienceId(audienceId)
      setSyncProgress('Starting sync...')

      const response = await apiClient.triggerMailchimpSync(syncType, audienceId)

      toast({
        title: 'Sync Started',
        description: `${syncType === 'full' ? 'Full' : 'Quick'} sync initiated for audience.`,
      })

      pollSyncStatus(response.sync_id)
    } catch (error: any) {
      console.error('Failed to trigger sync:', error)
      toast({
        title: 'Sync Failed',
        description: explainError(error, 'Failed to start sync'),
        variant: 'destructive',
      })
      setSyncing(false)
      setSyncProgress(null)
      setSyncingAudienceId(null)
    }
  }

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'Never'
    return (toDisplayDate(dateStr) ?? new Date(dateStr)).toLocaleString()
  }

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin" aria-label="Loading Mailchimp status" />
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Mail className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
          <CardTitle>Mailchimp Integration</CardTitle>
        </div>
        <CardDescription>
          Connect your Mailchimp account to sync contacts and audiences for email marketing
          campaigns
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Connection Status */}
        <div className="flex items-center justify-between p-4 border rounded-lg">
          <div className="flex items-center gap-3">
            {status?.connected ? (
              <>
                <CheckCircle
                  className="h-5 w-5"
                  style={{ color: CATEGORICAL.evergreen.solid }}
                  aria-hidden="true"
                />
                <div>
                  <p className="font-medium">Connected</p>
                  <p className="text-sm text-muted-foreground">
                    {status.integration?.account_name}
                    {status.integration?.server_prefix && (
                      <span className="ml-1 text-xs">(dc: {status.integration.server_prefix})</span>
                    )}
                  </p>
                </div>
              </>
            ) : (
              <>
                <AlertCircle className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
                <div>
                  <p className="font-medium">Not Connected</p>
                  <p className="text-sm text-muted-foreground">
                    Enter your Mailchimp API key to connect
                  </p>
                </div>
              </>
            )}
          </div>

          {status?.connected && (
            <Button
              variant="destructive"
              size="sm"
              onClick={handleDisconnect}
              disabled={loading || syncing}
              aria-label="Disconnect Mailchimp account"
            >
              <Unlink className="mr-2 h-4 w-4" aria-hidden="true" />
              Disconnect
            </Button>
          )}
        </div>

        {/* API Key Form (when not connected) */}
        {!status?.connected && (
          <form onSubmit={handleConnect} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="mailchimp-api-key">API Key</Label>
              <Input
                id="mailchimp-api-key"
                type="password"
                placeholder="Enter your Mailchimp API key (e.g., abc123def456-us2)"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                disabled={connecting}
                aria-describedby="mailchimp-api-key-help"
              />
              <p id="mailchimp-api-key-help" className="text-sm text-muted-foreground">
                Find your API key in Mailchimp under Account &rarr; Extras &rarr; API keys
              </p>
            </div>
            <Button
              type="submit"
              disabled={connecting || !apiKey.trim()}
              aria-label="Connect Mailchimp account"
            >
              {connecting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <Link2 className="mr-2 h-4 w-4" aria-hidden="true" />
              )}
              {connecting ? 'Connecting...' : 'Connect Mailchimp'}
            </Button>
          </form>
        )}

        {/* Audiences List with Sync Controls (when connected) */}
        {status?.connected &&
          status.integration?.audiences &&
          status.integration.audiences.length > 0 && (
            <div className="space-y-3">
              <h3 className="font-medium flex items-center gap-2">
                <Users className="h-4 w-4" aria-hidden="true" />
                Audiences
              </h3>

              <p className="text-sm text-muted-foreground">
                Choose the audience the CRM keeps in sync. New and updated contacts are pushed to it
                automatically, and Mailchimp updates (unsubscribes, bounces, spam complaints) are
                applied from it.
              </p>

              <div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePreview}
                  disabled={previewLoading}
                  aria-label="Preview what a full sync would push to Mailchimp"
                >
                  {previewLoading ? (
                    <Loader2 className="mr-2 h-3 w-3 animate-spin" aria-hidden="true" />
                  ) : (
                    <Eye className="mr-2 h-3 w-3" aria-hidden="true" />
                  )}
                  Preview sync
                </Button>
                <p className="mt-1 text-xs text-muted-foreground">
                  See what a full sync would push — and each record&rsquo;s resulting status —
                  without changing any Mailchimp records.
                </p>
              </div>

              {!selectedAudienceId && (
                <div
                  className="flex items-center gap-2 rounded-lg border p-3 text-sm"
                  style={{
                    backgroundColor: CATEGORICAL.clay.tint,
                    borderColor: CATEGORICAL.clay.solid,
                    color: GRACKLE_COLORS.prussianBlue,
                  }}
                  role="status"
                >
                  <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
                  Select a sync audience below to enable automatic contact sync.
                </div>
              )}

              {/* Sync Progress */}
              {syncing && syncProgress && (
                <div
                  className="flex items-center gap-2 p-3 border rounded-lg"
                  style={{
                    backgroundColor: SEMANTIC.info.fill,
                    borderColor: GRACKLE_COLORS.navy,
                  }}
                  role="status"
                  aria-live="polite"
                >
                  <Loader2
                    className="h-4 w-4 animate-spin"
                    style={{ color: '#FFFFFF' }}
                    aria-hidden="true"
                  />
                  <span className="text-sm" style={{ color: '#FFFFFF' }}>
                    {syncProgress}
                  </span>
                </div>
              )}

              <div className="border rounded-lg divide-y">
                {status.integration.audiences.map((audience) => (
                  <div key={audience.id} className="p-3 space-y-2">
                    <div className="flex items-start justify-between gap-3">
                      <label className="flex cursor-pointer items-start gap-2">
                        <input
                          type="radio"
                          name="mailchimp-sync-audience"
                          className="mt-1 h-4 w-4"
                          checked={selectedAudienceId === audience.id}
                          onChange={() => handleSetAudience(audience.id)}
                          disabled={!!settingAudienceId || syncing}
                          aria-label={`Use ${audience.name} as the sync audience`}
                        />
                        <span>
                          <span className="text-sm font-medium">{audience.name}</span>
                          <span className="block text-xs text-muted-foreground">
                            ID: {audience.id}
                          </span>
                        </span>
                      </label>
                      <div className="flex items-center gap-2">
                        {settingAudienceId === audience.id && (
                          <Loader2
                            className="h-3 w-3 animate-spin text-muted-foreground"
                            aria-label="Setting sync audience"
                          />
                        )}
                        {selectedAudienceId === audience.id && (
                          <Badge
                            className="border-0"
                            style={{
                              backgroundColor: SEMANTIC.success.fill,
                              color: SEMANTIC.success.text,
                            }}
                          >
                            Sync target
                          </Badge>
                        )}
                        <Badge variant="secondary">
                          {audience.member_count.toLocaleString()} members
                        </Badge>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleSync(audience.id, 'incremental')}
                        disabled={syncing}
                        aria-label={`Quick sync contacts to ${audience.name}`}
                      >
                        {syncing && syncingAudienceId === audience.id ? (
                          <Loader2 className="mr-2 h-3 w-3 animate-spin" aria-hidden="true" />
                        ) : (
                          <RefreshCw className="mr-2 h-3 w-3" aria-hidden="true" />
                        )}
                        Quick Sync
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleSync(audience.id, 'full')}
                        disabled={syncing}
                        aria-label={`Full sync all contacts to ${audience.name}`}
                      >
                        Full Sync
                      </Button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Last Sync Info */}
              {status.integration.last_sync_date && (
                <p className="text-xs text-muted-foreground">
                  Last synced: {formatDate(status.integration.last_sync_date)}
                </p>
              )}
            </div>
          )}

        {status?.connected &&
          status.integration?.audiences &&
          status.integration.audiences.length === 0 && (
            <p className="text-sm text-muted-foreground">
              No audiences found in this Mailchimp account. Create an audience in Mailchimp to get
              started.
            </p>
          )}

        <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Sync preview</DialogTitle>
              <DialogDescription>
                What a full sync would push, by each record&rsquo;s resulting Mailchimp status.
                Preview only — no Mailchimp records are changed.
              </DialogDescription>
            </DialogHeader>
            {previewLoading || !preview ? (
              <div
                className="flex items-center justify-center py-8"
                role="status"
                aria-live="polite"
              >
                <Loader2
                  className="h-5 w-5 animate-spin text-muted-foreground"
                  aria-hidden="true"
                />
                <span className="sr-only">Loading preview</span>
              </div>
            ) : (
              <div className="space-y-4">
                <dl className="grid grid-cols-3 gap-3 rounded-lg border p-3 text-center">
                  <div>
                    <dt className="text-xs text-muted-foreground">Subscribed (marketable)</dt>
                    <dd className="mt-1 text-lg font-semibold tabular-nums">
                      {preview.totals.subscribed.toLocaleString()}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs text-muted-foreground">Transactional</dt>
                    <dd className="mt-1 text-lg font-semibold tabular-nums">
                      {preview.totals.transactional.toLocaleString()}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs text-muted-foreground">Unsubscribed</dt>
                    <dd className="mt-1 text-lg font-semibold tabular-nums">
                      {preview.totals.unsubscribed.toLocaleString()}
                    </dd>
                  </div>
                </dl>

                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-muted-foreground">
                      <th className="py-1 text-left font-medium" scope="col">
                        Type
                      </th>
                      <th className="py-1 text-right font-medium" scope="col">
                        Total
                      </th>
                      <th className="py-1 text-right font-medium" scope="col">
                        Subscribed
                      </th>
                      <th className="py-1 text-right font-medium" scope="col">
                        Transactional
                      </th>
                      <th className="py-1 text-right font-medium" scope="col">
                        Unsubscribed
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {(['contacts', 'leads'] as const).map((k) => (
                      <tr key={k} className="border-t">
                        <td className="py-1 capitalize">{k}</td>
                        <td className="py-1 text-right tabular-nums">
                          {preview[k].total.toLocaleString()}
                        </td>
                        <td className="py-1 text-right tabular-nums">
                          {preview[k].subscribed.toLocaleString()}
                        </td>
                        <td className="py-1 text-right tabular-nums">
                          {preview[k].transactional.toLocaleString()}
                        </td>
                        <td className="py-1 text-right tabular-nums">
                          {preview[k].unsubscribed.toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <p className="text-xs text-muted-foreground">
                  {preview.distinctEmails.toLocaleString()} distinct email addresses — Mailchimp
                  de-duplicates by email, so the actual audience change may be smaller.
                </p>

                {preview.diff && (
                  <div className="space-y-2 rounded-lg border p-3">
                    <p className="text-sm font-medium">
                      Vs. current audience ({preview.diff.currentMembers.toLocaleString()} members)
                    </p>
                    <dl className="grid grid-cols-3 gap-3 text-center">
                      <div>
                        <dt className="text-xs text-muted-foreground">New</dt>
                        <dd className="mt-1 text-lg font-semibold tabular-nums">
                          {preview.diff.newMembers.toLocaleString()}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-xs text-muted-foreground">Updates</dt>
                        <dd className="mt-1 text-lg font-semibold tabular-nums">
                          {preview.diff.updates.toLocaleString()}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-xs text-muted-foreground">Downgrades</dt>
                        <dd className="mt-1 text-lg font-semibold tabular-nums">
                          {preview.diff.downgradeCount.toLocaleString()}
                        </dd>
                      </div>
                    </dl>
                    {preview.diff.downgradeCount > 0 && (
                      <div
                        className="flex items-start gap-2 rounded-lg border p-2 text-xs"
                        style={{
                          backgroundColor: SEMANTIC.error.fill,
                          borderColor: GRACKLE_COLORS.darkRaspberry,
                          color: '#FFFFFF',
                        }}
                        role="alert"
                      >
                        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                        <span>
                          <strong>{preview.diff.downgradeCount.toLocaleString()}</strong>{' '}
                          currently-subscribed member
                          {preview.diff.downgradeCount === 1 ? '' : 's'} would be moved off
                          Subscribed and stop receiving campaigns
                          {preview.diff.downgradeSample.length > 0 && (
                            <>
                              {' '}
                              (e.g.{' '}
                              {preview.diff.downgradeSample
                                .slice(0, 3)
                                .map((d) => d.email)
                                .join(', ')}
                              )
                            </>
                          )}
                          .
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {preview.diffWarning && (
                  <p className="text-xs text-muted-foreground">{preview.diffWarning}</p>
                )}

                <div
                  className="flex items-start gap-2 rounded-lg border p-2 text-xs"
                  style={{
                    backgroundColor: CATEGORICAL.clay.tint,
                    borderColor: CATEGORICAL.clay.solid,
                    color: GRACKLE_COLORS.prussianBlue,
                  }}
                  role="note"
                >
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                  <span>
                    Preview only — no Mailchimp records have been changed. Only{' '}
                    <strong>Subscribed</strong> contacts are marketable; Transactional contacts sit
                    in the audience but can&rsquo;t be sent campaigns.
                  </span>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  )
}
